// AI-generated bounding-box annotations for the capture flow (issue #42).
//
// Matches the visual style of AnnotationOverlay (inspector-drawn shapes) so the
// two feel like the same system: strokeWidth=44, strokeLinecap="round", 3-line
// open arrowheads, ellipses for ovals, and optional rotation on rect/oval.
//
// Coordinates are passed as fractions 0–1 (converted to SVG units ×10000
// internally) to decouple the caller from the SVG viewBox scale.

import React from 'react';

// ─── Public types ─────────────────────────────────────────────────────────────

export interface BBoxAnnotation {
  type: 'rect' | 'arrow' | 'oval';
  /** Fractions 0–1 (arrow: tail→tip; rect/oval: top-left→bottom-right bbox) */
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Clockwise rotation in degrees (0 = axis-aligned). Only for rect and oval. */
  rotationDeg?: number;
}

export interface BBoxFinding {
  /** 0-based index drives multi-finding colour palette */
  index: number;
  severity: 'safety_concern' | 'recommendation' | null;
  annotations: BBoxAnnotation[];
}

interface BBoxAnnotationOverlayProps {
  findings: BBoxFinding[];
}

// ─── Colour palette ───────────────────────────────────────────────────────────

const SEVERITY_COLOR: Record<string, string> = {
  safety_concern: '#C0392B',
  recommendation: '#D68910',
  observation: '#6B7280',
};

/** Palette when more than one finding is present — each gets a distinct colour */
const MULTI_PALETTE = ['#2563EB', '#C0392B', '#D68910', '#059669', '#7C3AED'];

// ─── Constants ────────────────────────────────────────────────────────────────

/** Matches AnnotationOverlay's strokeWidth so both tools feel the same */
const SW = 44;
/** Arrowhead arm length in SVG units — slightly longer than AnnotationOverlay's
 *  200 to make AI arrows visually prominent on small thumbnails */
const HEAD_LEN = 300;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function pickColor(finding: BBoxFinding, total: number): string {
  if (total === 1) {
    return SEVERITY_COLOR[finding.severity ?? 'observation'] ?? SEVERITY_COLOR.observation;
  }
  return MULTI_PALETTE[finding.index % MULTI_PALETTE.length];
}

/** Convert a 0–1 fraction to an integer SVG coordinate (viewBox 0–10000) */
const sv = (v: number) => v * 10000;

// ─── Component ────────────────────────────────────────────────────────────────

const BBoxAnnotationOverlay: React.FC<BBoxAnnotationOverlayProps> = ({ findings }) => {
  if (!findings || findings.length === 0) return null;

  const total = findings.length;

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox="0 0 10000 10000"
      preserveAspectRatio="none"
      aria-hidden
    >
      {findings.flatMap((finding) => {
        const c = pickColor(finding, total);
        return finding.annotations.map((ann, idx) => {
          const x1 = sv(ann.x1);
          const y1 = sv(ann.y1);
          const x2 = sv(ann.x2);
          const y2 = sv(ann.y2);
          const rot = ann.type !== 'arrow' ? (ann.rotationDeg ?? 0) : 0;
          const key = `${finding.index}-${idx}`;

          // ── Arrow ──────────────────────────────────────────────────────────
          // 3-line open arrowhead. Drawn twice: first a slightly wider white
          // outline pass, then the coloured pass on top — guarantees legibility
          // against any photo background (dark, light, or complex texture).
          if (ann.type === 'arrow') {
            const angle = Math.atan2(y2 - y1, x2 - x1);
            const lx = x2 - HEAD_LEN * Math.cos(angle - Math.PI / 6);
            const ly = y2 - HEAD_LEN * Math.sin(angle - Math.PI / 6);
            const rx = x2 - HEAD_LEN * Math.cos(angle + Math.PI / 6);
            const ry = y2 - HEAD_LEN * Math.sin(angle + Math.PI / 6);
            const outline = SW + 20;
            return (
              <g key={key}>
                {/* White outline pass for contrast */}
                <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="white" strokeWidth={outline} strokeLinecap="round" />
                <line x1={x2} y1={y2} x2={lx} y2={ly} stroke="white" strokeWidth={outline} strokeLinecap="round" />
                <line x1={x2} y1={y2} x2={rx} y2={ry} stroke="white" strokeWidth={outline} strokeLinecap="round" />
                {/* Coloured pass */}
                <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={c} strokeWidth={SW} strokeLinecap="round" />
                <line x1={x2} y1={y2} x2={lx} y2={ly} stroke={c} strokeWidth={SW} strokeLinecap="round" />
                <line x1={x2} y1={y2} x2={rx} y2={ry} stroke={c} strokeWidth={SW} strokeLinecap="round" />
              </g>
            );
          }

          const cx = (x1 + x2) / 2;
          const cy = (y1 + y2) / 2;
          const transform = rot !== 0 ? `rotate(${rot}, ${cx}, ${cy})` : undefined;

          // ── Oval ───────────────────────────────────────────────────────────
          if (ann.type === 'oval') {
            return (
              <ellipse
                key={key}
                cx={cx} cy={cy}
                rx={Math.abs(x2 - x1) / 2}
                ry={Math.abs(y2 - y1) / 2}
                stroke={c} strokeWidth={SW} fill="none"
                strokeLinecap="round"
                transform={transform}
              />
            );
          }

          // ── Rect (default) ─────────────────────────────────────────────────
          return (
            <rect
              key={key}
              x={Math.min(x1, x2)} y={Math.min(y1, y2)}
              width={Math.abs(x2 - x1)} height={Math.abs(y2 - y1)}
              stroke={c} strokeWidth={SW} fill="none"
              strokeLinejoin="round"
              transform={transform}
            />
          );
        });
      })}
    </svg>
  );
};

export default BBoxAnnotationOverlay;
